// Pure subscription / billing presentation logic extracted from Subscription.tsx
// and SubscriptionBanner.tsx so the state machine is unit-testable without the UI.
import type { Subscription } from '../types/subscription';

const SECONDS_PER_DAY = 24 * 60 * 60;

/** The plan always includes one base seat for the admin. */
export const BASE_USER_SEATS = 1;

/** Prices are held in cents, matching Stripe. */
export const BASE_PLAN_AMOUNT_CENTS = 25000;
export const ADDITIONAL_USER_AMOUNT_CENTS = 5000;

export type SubscriptionStatus = Subscription['status'];
export type SubscriptionStatusColor = 'success' | 'warning' | 'error' | 'default';

/**
 * Whole days between now and a Stripe epoch-seconds timestamp, floored at 0.
 * Returns 0 for a missing date, matching the UI's "unknown means none" default.
 */
export function getDaysRemaining(endDateSeconds: number | null, nowMs: number = Date.now()): number {
  if (!endDateSeconds) return 0;
  const nowSeconds = Math.floor(nowMs / 1000);
  return Math.max(0, Math.ceil((endDateSeconds - nowSeconds) / SECONDS_PER_DAY));
}

/**
 * MUI chip colour for a subscription status.
 */
export function getSubscriptionStatusColor(status: string): SubscriptionStatusColor {
  switch (status) {
    case 'active':
      return 'success';
    case 'past_due':
    case 'grace_period':
      return 'warning';
    case 'canceled':
    case 'unpaid':
      return 'error';
    default:
      return 'default';
  }
}

/** Format a cent amount as localised currency, e.g. 25000 -> "$250.00". */
export function formatAmount(amountCents: number, currency: string = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100);
}

/** Monthly total in cents for a given number of additional users. */
export function calculateMonthlyTotalCents(additionalUsers: number): number {
  return BASE_PLAN_AMOUNT_CENTS + additionalUsers * ADDITIONAL_USER_AMOUNT_CENTS;
}

export interface SeatChangePlan {
  /** True when the requested additional-seat count is below the current one. */
  isReduction: boolean;
  /** Seats the org would have after the change (base + additional). */
  newTotalSeats: number;
  /** How many active users must be deactivated first; 0 when the seats still fit. */
  requiredDeactivations: number;
}

/**
 * Work out whether a seat change needs users deactivated first.
 *
 * Behaviour preserved from Subscription.tsx: deactivation is only ever
 * required when reducing additional seats AND the resulting total seat count
 * is below the seats currently in use.
 */
export function planSeatChange(
  requestedAdditionalUsers: number,
  subscription: Pick<Subscription, 'additionalUserSeats' | 'usedUserSeats'>
): SeatChangePlan {
  const currentAdditionalSeats = subscription.additionalUserSeats || 0;
  const currentUsedSeats = subscription.usedUserSeats || 0;
  const newTotalSeats = BASE_USER_SEATS + requestedAdditionalUsers;
  const isReduction = requestedAdditionalUsers < currentAdditionalSeats;

  if (!isReduction || newTotalSeats >= currentUsedSeats) {
    return { isReduction, newTotalSeats, requiredDeactivations: 0 };
  }

  return {
    isReduction,
    newTotalSeats,
    requiredDeactivations: currentUsedSeats - newTotalSeats,
  };
}
